/**
 * AWS REST API (DynamoDB)
 */
import axios from "axios";
import axiosRetry from "axios-retry";
import DigitalEventStore from "@/store/index";

let aws_client = null;

/**
 * Create AwsRestClient instance for first request
 * @return {null}
 * @constructor
 */
function AwsRestClient() {
  if (!aws_client) {
    aws_client = axios.create({
      baseURL: DigitalEventStore.state.event.config.restApiConfig.url,
      headers: {
        common: {
          Authorization: DigitalEventStore.state.event.config.sessionUUID
        }
      }
    });
    axiosRetry(aws_client);
  }

  return aws_client;
}

export default {
  /**
   * Get table state
   * @param tableUUID {String}
   * @return {Promise<*[]>}
   */
  async getTableState({ tableUUID }) {
    return await AwsRestClient().get(`table_state/${tableUUID}`);
  },

  /**
   * User initialization in AWS DynamoDB
   * @param groups {Array}
   * @param profile {Object}
   * @return {Promise<*[]>}
   */
  async initUser({ groups, profile }) {
    return await AwsRestClient().post(`init_user`, {
      groups: groups,
      profile: profile
    });
  },

  /**
   * Get group state
   * @param roomUUID {String}
   * @param updatedAt {String}
   * @return {Promise<*[]>}
   */
  async getRoomState({ rooms, updatedAt }) {
    return await AwsRestClient().get(`room_state/`, { params: { updatedAt: updatedAt, rooms: rooms } });
  },

  /**
   * Get group messages
   * @param groups {Array}
   * @param messageType {String}
   * @return {Promise<*[]>}
   */
  async getGroupMessages({ groupUUID, messageType }) {
    let params = {};

    if (messageType) {
      params.message_type = messageType;
    }

    return await AwsRestClient()
      .get(`/${groupUUID}/messages`, {
        params,
        "axios-retry": {
          retries: 3,
          retryDelay: retryCount => {
            return retryCount * 1500; // time interval between retries
          },
          retryCondition: error => {
            return error.response.status === 400;
          }
        }
      })
      .catch(() => {
        // eslint-disable-next-line no-console
        console.warn("Can not get group messages, please contact support.");
      });
  },

  /**
   * Get user's invites
   * @return {Promise<*[]>}
   */
  async getInvites() {
    return await AwsRestClient().get("invites");
  },

  /**
   * Get current user connections
   * @return {Promise<*[]>}
   */
  async getUserConnections() {
    return await AwsRestClient().get(`connections`);
  },

  /**
   * Create new user connection
   * @param targetUserUUID {String}
   * @return {Promise<*>}
   */
  async createUserConnection(targetUserUUID) {
    return await AwsRestClient().post("connections", {
      target: targetUserUUID
    });
  },

  /**
   * Update existing user connection
   * @param targetUserUUID {String}
   * @param data {Object}
   * @return {Promise<*>}
   */
  async updateUserConnection(targetUserUUID, data) {
    return await AwsRestClient().put(`connections/${targetUserUUID}`, data);
  },

  async getPrivateChatsHistory() {
    return await AwsRestClient().get("private_messages");
  },

  /**
   * Update user's info
   * @param data {Object}
   * @return {Promise<*>}
   */
  async updateUserInfo(data) {
    return await AwsRestClient().put("user_info", data);
  },

  /**
   * Get user connectivity
   * @return {Promise<*>}
   */
  async getUserConnectivity() {
    return await AwsRestClient().get("connectivity");
  },

  /**
   * Set user connectivity
   * @param data {Object}
   * @return {Promise<*>}
   */
  async setUserConnectivity(data) {
    return await AwsRestClient().post("connectivity", data);
  },

  /**
   * Get user's video chat list
   * @return {Promise<*>}
   */
  async getVideoChats() {
    return await AwsRestClient().get("video_chats");
  },

  /**
   * Create video chat
   * @param params {Object}
   * @return {Promise<*>}
   */
  async createVideoChat(params) {
    return await AwsRestClient().post("video_chats", params);
  },

  /**
   * Update video chat info
   * @param videoChatUUID {String}
   * @param data {Object}
   * @return {Promise<*>}
   */
  async updateVideoChat(videoChatUUID, data) {
    return await AwsRestClient().put(`video_chats/${videoChatUUID}`, data);
  },

  /**
   * Delete video chat
   * @param videoChatUUID {String}
   * @return {Promise<*>}
   */
  async deleteVideoChat(videoChatUUID) {
    return await AwsRestClient().delete(`video_chats/${videoChatUUID}`);
  },

  /**
   * Leave video chat
   * @param videoChatUUID {String}
   * @return {Promise<*>}
   */
  async leaveVideoChat(videoChatUUID) {
    return await AwsRestClient().post(`video_chats/${videoChatUUID}/leave`);
  },

  /**
   * Join to the video call
   * @param videoChatUUID {String}
   * @param data {Object}
   * @return {Promise<*>}
   */
  async joinVideoCall(videoChatUUID, data) {
    return await AwsRestClient().post(`video_chats/${videoChatUUID}/join`, data);
  },

  /**
   * Leave video call
   * @param videoChatUUID {String}
   * @return {Promise<*>}
   */
  async leaveVideoCall(videoChatUUID) {
    return await AwsRestClient().post(`video_chats/${videoChatUUID}/leave_call`);
  },

  /**
   * Update group message
   * @param groupUUID {String}
   * @param messageUUID {String}
   * @param message {Object}
   * @return {Promise<AxiosResponse<any>>}
   */
  async updateGroupMessage({ groupUUID, messageUUID, message }) {
    return await AwsRestClient().put(`${groupUUID}/messages/${messageUUID}`, {
      message: message
    });
  },

  /**
   * Delete group message
   * @param groupUUID {String}
   * @param messageUUID {String}
   * @param messageType {Object}
   * @return {Promise<AxiosResponse<any>>}
   */
  async deleteMessage({ groupUUID, messageUUID, messageType = null }) {
    let params = {};

    if (messageType) {
      params.message_type = messageType;
    }

    return await AwsRestClient().delete(`${groupUUID}/messages/${messageUUID}`, { params: params });
  },
  async changeRoom({ enterRoomUUID }) {
    return await AwsRestClient().post(`/room/${enterRoomUUID}/enter`);
  }
};
